import * as echarts from 'echarts';
import type { ECharts } from 'echarts';
import { classRegistry, FabricObject } from 'fabric';
import { EMPTY_GAUGE_CONFIG, MnemonicGaugeConfig, toGaugeEChartsOption } from './mnemonic-gauge.mapper';
import {
    createOffscreenEChartsContainer,
    drawEChartsCanvas,
    resolveMnemonicEChartsRenderScale,
    resolveMnemonicEChartsSize,
} from './mnemonic-echarts-host.util';

const GAUGE_PROPS = ['gauge'] as const;

export class MnemonicGauge extends FabricObject {
    declare gauge: MnemonicGaugeConfig;
    declare name: string;

    private gaugeInstance?: ECharts;
    private gaugeContainer?: HTMLDivElement;
    private renderScale = 2;

    static override getDefaults() {
        return {
            ...super.getDefaults(),
            ...MnemonicGauge.ownDefaults,
        };
    }

    constructor(options: Record<string, unknown> = {}) {
        super();
        Object.assign(this, MnemonicGauge.ownDefaults);
        this.setOptions(options);
        this.gauge = (options.gauge as MnemonicGaugeConfig) ?? { ...EMPTY_GAUGE_CONFIG };
        this.createGaugeInstance();
    }

    updateGaugeConfig(gaugeConfig: MnemonicGaugeConfig): void {
        this.gauge = {
            ...this.gauge,
            ...gaugeConfig,
            options: {
                ...this.gauge.options,
                ...gaugeConfig.options,
            },
        };
        this.syncGaugeInstance();
    }

    override set(key: string | Record<string, unknown>, value?: unknown): this {
        const gaugePayload =
            typeof key === 'object' && key !== null && 'gauge' in key
                ? (key.gauge as MnemonicGaugeConfig)
                : key === 'gauge'
                  ? (value as MnemonicGaugeConfig)
                  : null;

        const result = super.set(key as never, value as never);

        if (gaugePayload) {
            this.updateGaugeConfig(gaugePayload);
        }

        if (
            (typeof key === 'object' && key !== null && ('width' in key || 'height' in key || 'scaleX' in key || 'scaleY' in key)) ||
            key === 'width' ||
            key === 'height' ||
            key === 'scaleX' ||
            key === 'scaleY'
        ) {
            this.resizeGauge();
        }

        return result;
    }

    override toObject(propertiesToInclude: string[] = []): Record<string, unknown> {
        return super.toObject([...GAUGE_PROPS, ...propertiesToInclude]);
    }

    override _render(ctx: CanvasRenderingContext2D): void {
        this.ensureRenderResolution();

        const width = this.width;
        const height = this.height;
        const x = -width / 2;
        const y = -height / 2;

        ctx.fillStyle = (this.fill as string) || '#ffffff';
        ctx.fillRect(x, y, width, height);

        const gaugeCanvas = this.getGaugeCanvas();
        if (gaugeCanvas) {
            drawEChartsCanvas(ctx, gaugeCanvas, x, y, width, height);
        }
    }

    override dispose(): void {
        this.destroyGaugeInstance();
        super.dispose();
    }

    static override async fromObject(object: Record<string, unknown>): Promise<MnemonicGauge> {
        return new MnemonicGauge(object);
    }

    private createGaugeInstance(): void {
        this.destroyGaugeInstance();

        const { width, height } = resolveMnemonicEChartsSize(this);
        this.renderScale = resolveMnemonicEChartsRenderScale(this);
        this.gaugeContainer = createOffscreenEChartsContainer(width, height);

        this.gaugeInstance = echarts.init(this.gaugeContainer, undefined, {
            renderer: 'canvas',
            width,
            height,
            devicePixelRatio: this.renderScale,
        });

        this.gaugeInstance.setOption(toGaugeEChartsOption(this.gauge, this.name, width), true);
        this.dirty = true;
    }

    private ensureRenderResolution(): void {
        const nextScale = resolveMnemonicEChartsRenderScale(this);

        if (nextScale === this.renderScale && this.gaugeInstance) {
            return;
        }

        this.renderScale = nextScale;

        if (!this.gaugeContainer) {
            return;
        }

        const { width, height } = resolveMnemonicEChartsSize(this);
        this.gaugeInstance?.dispose();
        this.gaugeInstance = echarts.init(this.gaugeContainer, undefined, {
            renderer: 'canvas',
            width,
            height,
            devicePixelRatio: this.renderScale,
        });
        this.gaugeInstance.setOption(toGaugeEChartsOption(this.gauge, this.name, width), true);
        this.dirty = true;
    }

    private syncGaugeInstance(): void {
        if (!this.gaugeInstance) {
            this.createGaugeInstance();
            this.canvas?.requestRenderAll();
            return;
        }

        this.gaugeInstance.setOption(toGaugeEChartsOption(this.gauge, this.name, resolveMnemonicEChartsSize(this).width), true);
        this.dirty = true;
        this.canvas?.requestRenderAll();
    }

    private resizeGauge(): void {
        if (!this.gaugeInstance || !this.gaugeContainer) {
            return;
        }

        const { width, height } = resolveMnemonicEChartsSize(this);
        const nextScale = resolveMnemonicEChartsRenderScale(this);

        this.gaugeContainer.style.width = `${width}px`;
        this.gaugeContainer.style.height = `${height}px`;

        if (nextScale !== this.renderScale) {
            this.renderScale = nextScale;
            this.gaugeInstance.dispose();
            this.gaugeInstance = echarts.init(this.gaugeContainer, undefined, {
                renderer: 'canvas',
                width,
                height,
                devicePixelRatio: this.renderScale,
            });
            this.gaugeInstance.setOption(toGaugeEChartsOption(this.gauge, this.name, width), true);
        } else {
            this.gaugeInstance.resize({ width, height });
        }

        this.dirty = true;
    }

    private getGaugeCanvas(): HTMLCanvasElement | null {
        return (this.gaugeInstance?.getDom().querySelector('canvas') as HTMLCanvasElement | null) ?? null;
    }

    private destroyGaugeInstance(): void {
        this.gaugeInstance?.dispose();
        this.gaugeInstance = undefined;
        this.gaugeContainer?.remove();
        this.gaugeContainer = undefined;
    }
}

Object.defineProperty(MnemonicGauge, 'type', { value: 'gauge' });
Object.defineProperty(MnemonicGauge, 'ownDefaults', {
    value: {
        width: 280,
        height: 280,
        fill: '#ffffff',
        stroke: null,
        strokeWidth: 0,
        originX: 'left',
        originY: 'top',
        objectCaching: false,
    },
});
Object.defineProperty(MnemonicGauge, 'customProperties', {
    value: ['gauge', 'id', 'name', 'objectType', 'tooltip', 'deviceId', 'variableId', 'selectOption'],
});

let isGaugeClassRegistered = false;

export function registerMnemonicGaugeClass(): void {
    if (isGaugeClassRegistered) {
        return;
    }

    classRegistry.setClass(MnemonicGauge, 'gauge');
    isGaugeClassRegistered = true;
}
